/**
 * Email Results modal (Step 4).
 *
 * Reuses the existing `POST /api/generate/email` (attaches each selected GenerationItem's bundle). The
 * flow run produced ONE job whose items are the (work order × format) combos; we fetch the job detail
 * to list those items and let the user choose which to attach. Subject + Message are prefilled and
 * editable; recipients are comma-separated. After sending, the caller shows a "Generation complete"
 * summary.
 */
import { useEffect, useMemo, useState } from "react";
import { useGenerationJob, useEmailEdds } from "../../api/generation";
import { errorMessage } from "../../api/client";
import { Modal } from "../../components/Modal";
import { Button } from "../../components/Button";
import { TextField, TextArea } from "../../components/TextField";
import { Spinner } from "../../components/Spinner";
import { useToast } from "../../components/Toast";
import { track, EVENTS } from "./telemetry";

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function parseEmails(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function EmailModal({
  open,
  onClose,
  jobId,
  workOrderLabel,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  jobId: string;
  workOrderLabel: string;
  onSent: () => void;
}) {
  const toast = useToast();
  const jobQ = useGenerationJob(open ? jobId : undefined);
  const email = useEmailEdds();

  const items = useMemo(
    () => (jobQ.data?.items ?? []).filter((i) => i.files.length > 0),
    [jobQ.data],
  );

  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  // prefill once the modal opens / items load
  useEffect(() => {
    if (!open) return;
    setSubject(`EDD Deliverables - ${workOrderLabel} - ${todayISO()}`);
    setMessage(
      `Hi,\n\nPlease find the attached EDD deliverables for ${workOrderLabel}.\n\nThank you.`,
    );
  }, [open, workOrderLabel]);

  useEffect(() => {
    // default: attach everything that produced a file
    setPicked(new Set(items.map((i) => i.id)));
  }, [items]);

  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const recipients = parseEmails(to);
  const canSend = recipients.length > 0 && picked.size > 0 && !email.isPending;

  const send = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one recipient email.");
      return;
    }
    if (picked.size === 0) {
      toast.error("Choose at least one file to attach.");
      return;
    }
    try {
      const result = await email.mutateAsync({
        item_ids: [...picked],
        to: recipients,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      });
      track(EVENTS.edd_downloaded, { delivery_method: "email", count: picked.size });
      if (result.sent) {
        toast.success(`Emailed ${result.count} file${result.count === 1 ? "" : "s"} to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}.`);
      } else if (!result.configured) {
        toast.info("Email isn't set up on this server yet — the request was recorded but nothing was sent.");
      } else {
        toast.error(result.message || "The email could not be sent.");
      }
      onSent();
      onClose();
    } catch (err) {
      track(EVENTS.error_encountered, { page: "generate_flow_email", error_type: "email_failed" });
      toast.error(errorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Email results"
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={email.isPending} disabled={!canSend} onClick={send}>
            Send
          </Button>
        </>
      }
    >
      <TextField
        label="To"
        placeholder="client@example.com, regulator@agency.gov"
        hint="Separate multiple recipients with commas."
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <TextField label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
      <TextArea label="Message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />

      <div className="stack" style={{ gap: "var(--space-1)" }}>
        <strong>Files to attach</strong>
        {jobQ.isLoading ? (
          <Spinner label="Loading files…" />
        ) : items.length === 0 ? (
          <p className="muted">This run produced no downloadable files.</p>
        ) : (
          items.map((it) => (
            <label key={it.id} className="checkbox">
              <input type="checkbox" checked={picked.has(it.id)} onChange={() => toggle(it.id)} />
              {it.label}
              <span className="faint"> · {it.files.length} file{it.files.length === 1 ? "" : "s"}</span>
            </label>
          ))
        )}
      </div>
    </Modal>
  );
}
